import { LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { supportsHover } from '../../../classes/supports-hover';
import { MenuItem } from '../../../interfaces/website-menu';
import { StoryblokCdnPipe } from '../../../storyblok/pipes/storyblok-cdn.pipe';
import { storyblokLinkPipe } from '../../../storyblok/pipes/storyblok-link.pipe';
import { ButtonDirective } from '../../directives/button.directive';
import { SvgComponent } from '../svg/svg.component';

/** Grace before hover-close so the pointer can cross the trigger → panel gap. */
const HOVER_CLOSE_MS = 150;

/**
 * Marketing-header dropdown. Deliberately NOT app-context-menu: that one
 * materializes its content into a global overlay only on open, so the links
 * never exist in server HTML and crawlers (and non-hovering bots) can't see
 * them. Here the panels are always in the DOM — closed state is CSS + `inert`.
 */
@Component({
  selector: 'app-website-desktop-menu',
  templateUrl: './website-desktop-menu.component.html',
  styleUrls: ['./website-desktop-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, RouterLink, SvgComponent, LowerCasePipe, StoryblokCdnPipe, storyblokLinkPipe],
  host: {
    '(document:keydown.escape)': 'closeAll()',
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
  },
})
export class WebsiteDesktopMenuComponent {
  private host = inject(ElementRef<HTMLElement>);

  readonly menuStructure = input<MenuItem[]>([]);

  /** Text of the open dropdown item, or null. Only one open at a time. */
  readonly openDropdown = signal<string | null>(null);
  private closeTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.closeTimer));
  }

  toggle(text: string) {
    clearTimeout(this.closeTimer);
    this.openDropdown.set(this.openDropdown() === text ? null : text);
  }

  hoverOpen(text: string) {
    if (!supportsHover()) return;
    clearTimeout(this.closeTimer);
    this.openDropdown.set(text);
  }

  hoverClose(text: string) {
    if (!supportsHover()) return;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (this.openDropdown() === text) this.openDropdown.set(null);
    }, HOVER_CLOSE_MS);
  }

  closeAll() {
    clearTimeout(this.closeTimer);
    this.openDropdown.set(null);
  }

  onDocumentPointerDown(event: Event) {
    if (!this.host.nativeElement.contains(event.target as Node)) this.closeAll();
  }
}
